var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, EmptyState, FlexBox, HelpIcon, Pagination, Table, Text, } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { useCallback, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { analytics } from 'app/utils/segment';
import { TableInstrumentDataValue } from './components/dataValue/tableInstrumentDataValue';
import styles from './tableInstrument.module.scss';
import { useTableInstrumentRows } from './utils/useTableInstrumentRows/useTableInstrumentRows';
export const TableInstrument = ({ instrument, panel, onReadInstruments, }) => {
    var _a;
    const { title, data, data: { columns, options }, } = instrument;
    const { dashboard } = useAnalyticsState();
    const [sortStatus, setSortStatus] = useState(null);
    const tableWrapperRef = useRef(null);
    const limit = (_a = options === null || options === void 0 ? void 0 : options.limit) !== null && _a !== void 0 ? _a : 50;
    const { rows, currentPage, totalPages, setPage } = useTableInstrumentRows({
        data,
        limit,
        sortStatus,
    });
    const isEmpty = data.rows.length === 0;
    /** Whether to show that not all rows have been returned. */
    const showLimiter = !!(options === null || options === void 0 ? void 0 : options.next_page);
    /** Whether to show the pagination when all rows have been returned. */
    const showPagination = !showLimiter && totalPages > 1;
    // Scrolls to the top of the table when the page changes.
    useUpdateEffect(() => {
        if (tableWrapperRef.current) {
            tableWrapperRef.current.scrollTop = 0;
        }
    }, [currentPage]);
    const handleLoadAll = useCallback(() => {
        analytics.analyticsTableLoadAllClicked({
            panel: panel.title,
            dashboard: dashboard.title,
        });
        return onReadInstruments({
            meta: {
                pagination: {
                    limit: null,
                },
            },
        });
    }, [dashboard.title, onReadInstruments, panel.title]);
    const handleSortChange = useCallback((newSortStatus) => __awaiter(void 0, void 0, void 0, function* () {
        // When sorting a table with limited results, we automatically load all the results and then
        // update the sorting, in order to ensure that the sorting is applied to all the results.
        if (showLimiter) {
            yield handleLoadAll();
        }
        setSortStatus(newSortStatus);
        analytics.analyticsTableSortUpdated({
            column: newSortStatus === null || newSortStatus === void 0 ? void 0 : newSortStatus.sortBy,
            panel: panel.title,
            dashboard: dashboard.title,
        });
    }), [dashboard.title, handleLoadAll, panel.title, showLimiter]);
    return (_jsxs(_Fragment, { children: [title && (_jsx("div", { children: _jsx(Text.Heading, Object.assign({ variant: "heading-4" }, { children: title })) })), _jsxs(Table, Object.assign({ stickyHeader: true, sortingProps: {
                    sortStatus,
                    onSortChange: handleSortChange,
                }, wrapperProps: {
                    className: classNames(styles.tableWrapper, {
                        [styles.tableWrapperEmpty]: isEmpty,
                    }),
                }, wrapperRef: tableWrapperRef, hoverable: !isEmpty }, { children: [_jsx(Table.Thead, { children: _jsx(Table.Tr, { children: columns.map(({ key, label, sortable }) => (_jsx(Table.Th, Object.assign({ className: styles.thCell, columnId: sortable ? key : null }, { children: label }), label))) }) }), _jsxs(Table.Tbody, { children: [isEmpty && (_jsx(Table.Tr, { children: _jsx(Table.Td, Object.assign({ colSpan: columns.length }, { children: _jsx(EmptyState, { title: "No data available", description: "There is no data available for the selected period and filters.", icon: "th" }) })) })), rows.map((row, rowIndex) => {
                                const rowKey = `row-${rowIndex}`;
                                return (_jsx(Table.Tr, { children: columns.map(({ key, format }, cellIndex) => {
                                        const cell = row.cells.find((cell) => cell.column === key);
                                        return (_jsx(Table.Td, Object.assign({ className: styles.tdCell }, { children: _jsx(TableInstrumentDataValue, { panel: panel, cell: cell, format: format }) }), `${rowKey}-cell-${cellIndex}`));
                                    }) }, rowKey));
                            })] }), showLimiter && (_jsx(Table.Footer, { children: _jsxs(FlexBox, Object.assign({ alignItems: "center", justifyContent: "center", gap: "300" }, { children: [_jsxs(FlexBox, Object.assign({ gap: "100", alignItems: "center" }, { children: [_jsx(HelpIcon, { tooltipClassName: styles.tooltip, text: `By default we only load the first ${limit} results. If you need to see all available results, click on the button, but beware that this may take a bit of time to load depending on the number of results.`, dark: true }), _jsxs(Text.Paragraph, Object.assign({ variant: "2" }, { children: ["Showing only the ", _jsxs("b", { children: ["first ", limit, " results"] })] }))] })), _jsx(Button, Object.assign({ variant: "secondary", onClick: handleLoadAll }, { children: "Load all results" }))] })) })), showPagination && (_jsx(Table.Footer, { children: _jsx(FlexBox, Object.assign({ alignItems: "center", justifyContent: "center" }, { children: _jsx(Pagination, { pageCount: totalPages, currentPage: currentPage, pageRangeDisplayed: 6, onPageChange: setPage }) })) }))] }))] }));
};
